// Components
import { Box, Group, Text, Button, Select, ActionIcon } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { PhoneInput } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";

// Constants
import {
  TELECOM_SYSTEM_OPTIONS,
  TELECOM_USE_OPTIONS,
} from "@benchmark/core/constants";

// Types
import type { ComboboxProps } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type { ContactPoint } from "@medplum/fhirtypes";

// Utils
import { findDuplicatePhoneLikeTelecomIndices } from "@benchmark/core/utils";
import { telecomValidationSchemas } from "@benchmark/ui/utils";

/**
 * Props for ContactDetailsSection component.
 * @agentic @benchmark/core
 */
type ContactDetailsProps<T = Record<string, unknown>> = {
  form: UseFormReturnType<T>;
  fieldPath: string;
  title?: string;
  allowedSystems?: readonly { value: string; label: string }[];
  maxContacts?: number;
  /** Forwarded to all inner Select components (e.g. to pin dropdown position). */
  comboboxProps?: ComboboxProps;
};

/**
 * Form section for editing FHIR ContactPoint telecom entries (phone, email, etc.).
 * @agentic @benchmark/core
 */
export const ContactDetailsSection = <T extends Record<string, unknown>>({
  form,
  fieldPath,
  title,
  allowedSystems = TELECOM_SYSTEM_OPTIONS,
  maxContacts = 5,
  comboboxProps,
}: ContactDetailsProps<T>) => {
  const contacts = (form.values[fieldPath] as ContactPoint[] | undefined) || [];

  const duplicatePhoneIndices = findDuplicatePhoneLikeTelecomIndices(
    contacts,
    telecomValidationSchemas.validateContactValue,
  );

  const addContact = () => {
    if (contacts.length < maxContacts) {
      // Type assertion needed due to Mantine v8 form type inference changes
      (form.insertListItem as (path: string, item: ContactPoint) => void)(
        fieldPath,
        {
          system: "phone",
          value: "",
          use: "work",
        },
      );
    }
  };

  const removeContact = (index: number) => {
    form.removeListItem(fieldPath, index);
  };

  // Validation function for contact values based on system type
  const validateContactValue = (
    value: string,
    system: string,
  ): string | null => {
    return telecomValidationSchemas.validateContactValue(value, system);
  };

  return (
    <Box>
      <Group justify="space-between" mb="sm">
        <Text size="sm" fw={500}>
          {title}
        </Text>
      </Group>

      {contacts.map((contact: ContactPoint, index: number) => {
        const currentSystem = contact.system || "phone";
        const currentValue = contact.value || "";
        const validationError = validateContactValue(
          currentValue,
          currentSystem,
        );
        const duplicatePhoneError = duplicatePhoneIndices.has(index)
          ? "This phone number is already listed"
          : null;
        const valueError = duplicatePhoneError ?? validationError;
        const isPhoneLikeSystem =
          currentSystem === "phone" ||
          currentSystem === "sms" ||
          currentSystem === "fax";

        return (
          <Group key={index} align="flex-end" mb="sm">
            <Select
              label="Type"
              placeholder="Contact type"
              data={allowedSystems}
              style={{ flex: 1 }}
              comboboxProps={comboboxProps}
              {...form.getInputProps(`${fieldPath}.${index}.system`)}
            />
            {isPhoneLikeSystem ? (
              <PhoneInput
                label="Value"
                placeholder="Enter phone number"
                style={{ flex: 2 }}
                error={valueError}
                {...form.getInputProps(`${fieldPath}.${index}.value`)}
              />
            ) : (
              <TextInput
                label="Value"
                placeholder="Enter contact information"
                style={{ flex: 2 }}
                error={valueError}
                {...form.getInputProps(`${fieldPath}.${index}.value`)}
              />
            )}
            <Select
              label="Use"
              placeholder="Purpose"
              data={TELECOM_USE_OPTIONS}
              style={{ flex: 1 }}
              comboboxProps={comboboxProps}
              {...form.getInputProps(`${fieldPath}.${index}.use`)}
            />
            <ActionIcon
              color="red"
              variant="light"
              onClick={() => removeContact(index)}
              style={{ marginBottom: 2 }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        );
      })}

      {contacts.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No contact details added yet. Click "Add Contact" to get started.
        </Text>
      )}

      <Button
        mt="sm"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={addContact}
        disabled={contacts.length >= maxContacts}
        fullWidth
      >
        Add Contact
      </Button>
    </Box>
  );
};
